import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber, Form, Typography } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../components/ImageSelector';
import RichEditor from '../../components/RichEditor';
import AllCategorySelector from '../../components/AllCategorySelector';

const Teachers = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchCatUid, setSearchCatUid] = useState('');
    const [searchCat2Uid, setSearchCat2Uid] = useState('');
    const [formApi, setFormApi] = useState(null);

    const statusMap = {
        0: { text: '未审核', type: 'warning' },
        1: { text: '已通过', type: 'success' },
        2: { text: '已拒绝', type: 'danger' },
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/o2o/list_staff_user/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    cat_uid: searchCatUid,
                    cat2_uid: searchCat2Uid
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/o2o/add_staff_user/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/o2o/edit_staff_user/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/o2o/delete_staff_user/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的教师');
            return;
        }
        try {
            const response = await request.post('/go/sp/o2o/batchedit_staff_user/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '教师信息',
            dataIndex: 'name',
            width: 300,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {record.avatar && <img src={record.avatar} alt={text} style={{ width: 40, height: 40, borderRadius: 4 }} />}
                    <div style={{ fontSize: '12px' }}>
                        <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>职称: {record.title}</div>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>{record.title2}</div>
                        {record.cat && <div style={{ color: 'var(--semi-color-text-2)' }}>课程: {record.cat.name}</div>}
                        {record.cat2 && <div style={{ color: 'var(--semi-color-text-2)' }}>年级: {record.cat2.name}</div>}
                    </div>
                </div>
            )
        },

        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 100
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该教师吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            content: typeof currentRecord.content === 'string' ?
                JSON.parse(currentRecord.content) : currentRecord.content
        } : {
            cat_uid: '',
            su_uid: '',
            store_uid: '',
            status: 0,
            sort: 0,
            title: '',
            title2: '',
            name: '',
            avatar: '',
            main_img: '',
            video_url: '',
            content: {
                html: '',
                images: [],
                assoc_uids: []
            },
            like_cnt: 0,
            fav_cnt: 0,
            comment_cnt: 0,
            click_cnt: 0,
            service_cnt: 0,
            score_cnt: 0,
            score_total: 0
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    const submitValues = {
                        ...values,
                        content: JSON.stringify(values.content)
                    };
                    if (isEdit) {
                        handleEdit(submitValues);
                    } else {
                        handleAdd(submitValues);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Input
                    field="name"
                    label="教师姓名"
                    rules={[{ required: true, message: '请输入教师姓名' }]}
                />
                <Form.Input
                    field="title"
                    label="职称"
                    rules={[{ required: true, message: '请输入职称' }]}
                />
                <Form.Input
                    field="title2"
                    label="副标题"
                    placeholder="可以写优势、特长等"
                />
                <Form.Slot label="头像">
                    <ImageSelector
                        value={formApi?.getValue('avatar')}
                        onChange={value => formApi?.setValue('avatar', value)}
                        placeholder="请上传头像"
                        width="100%"
                        previewSize={100}
                    />
                </Form.Slot>
                <Form.Slot label="主图海报">
                    <Typography.Text type="tertiary" style={{ marginBottom: '8px', display: 'block' }}>建议图片尺寸为900*500</Typography.Text>
                    <ImageSelector
                        value={formApi?.getValue('main_img')}
                        onChange={value => formApi?.setValue('main_img', value)}
                        placeholder="请上传主图海报"
                        width="100%"
                        previewSize={180}
                        previewBorderRadius={4}
                    />
                </Form.Slot>
                <Form.Input
                    field="video_url"
                    label="介绍视频"
                    placeholder="请输入视频URL"
                />
                <Form.Select
                    field="status"
                    label="状态"
                    initValue={0}
                >
                    <Select.Option value={0}>未审核</Select.Option>
                    <Select.Option value={1}>已通过</Select.Option>
                    <Select.Option value={2}>已拒绝</Select.Option>
                </Form.Select>
                <Form.InputNumber
                    field="sort"
                    label="排序"
                    initValue={0}
                    min={0}
                />
                <Form.Slot label="课程分类">
                    <AllCategorySelector
                        value={formApi?.getValue('cat_uid')}
                        onChange={value => formApi?.setValue('cat_uid', value)}
                        typ={2}
                        style={{ width: '100%' }}
                    />
                </Form.Slot>
                <Form.Slot label="年级分类">
                    <AllCategorySelector
                        value={formApi?.getValue('cat2_uid')}
                        onChange={value => formApi?.setValue('cat2_uid', value)}
                        typ={1}
                        style={{ width: '100%' }}
                    />
                </Form.Slot>
                <Form.Slot label="详细介绍">
                    <RichEditor
                        value={formApi?.getValue('content.html') || ''}
                        onChange={value => {
                            formApi?.setValue('content', {
                                html: value,
                                images: [],
                                assoc_uids: []
                            });
                        }}
                    />
                </Form.Slot>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.teachersContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索教师姓名"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="审核状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <AllCategorySelector
                        typ={2}
                        value={searchCatUid}
                        onChange={setSearchCatUid}
                        placeholder="课程分类"
                        style={{ width: 150 }}
                    />
                    <AllCategorySelector
                        typ={1}
                        value={searchCat2Uid}
                        onChange={setSearchCat2Uid}
                        placeholder="年级分类"
                        style={{ width: 150 }}
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增教师</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(2, selectedRowKeys)}>批量拒绝</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量通过</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个教师吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增教师"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={800}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden' }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑教师"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={800}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden' }}
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default Teachers;